/**
 * What the install offer remembers — which is entirely "have we already asked,
 * and how did that go".
 *
 * An install prompt is the one piece of UI on this site that interrupts. That
 * makes restraint the whole design, and restraint is a storage problem: the
 * offer has to know it is not the first page load, that it already had its say
 * three weeks ago, and that it has now been waved away enough times to stop
 * asking. None of that survives a reload without being written down.
 *
 * Three rules, in `isOfferDue` below:
 *
 *  - **not on the first load.** A prompt to install a site you have looked at
 *    for four seconds is a prompt for a site you have not decided you want.
 *    Waiting for a second document load costs nothing and means every visitor
 *    who sees this has come back at least once.
 *  - **a long snooze, started by being seen.** Dismissal means "not now", not
 *    "never" — but so does silence, and this offer cannot tell a visitor who
 *    considered it and moved on from one who never looked at the bottom of the
 *    page. Both have to end the asking for a while, or the panel arrives on
 *    every single load until it is finally clicked, which on iOS is forever:
 *    Safari has no `appinstalled` event and a tab cannot see a home-screen
 *    copy of itself, so a visitor who follows the steps successfully would go
 *    on being asked. `askedAt` is therefore stamped when the offer is shown
 *    (`recordImpression`) as well as when it is dismissed. Thirty days is
 *    tuned for a site somebody opens most days — on one visited that often, a
 *    ten-day snooze would still be a monthly nag. Tune `SNOOZE_MS` down if
 *    this app is visited rarely enough that a month means "never again".
 *  - **a hard stop.** Three dismissals is an answer. After that the offer is
 *    gone for good and installing is left to the browser's own menu, which is
 *    where it lives anyway — and which ./install-prompt.ts hands back by not
 *    suppressing the browser's own prompt once `isOfferSilenced` is true.
 *
 * Only a dismissal spends a strike. Being ignored buys silence but never
 * counts as a no, because "tapped nothing" is not an answer to record.
 *
 * The key and the storage handle are the app's, not this module's. An app
 * makes one store with `createInstallStateStore` — retells passes
 * `storageKey("install.v1")` and `resolveLocalStorage` from its own
 * src/storage.ts — and hands that one object to ./install-prompt.ts and
 * ../hooks/use-install-offer.ts, so the two can never be reading different
 * keys. The version is in that key rather than the payload, every read is
 * total — absent key, malformed JSON, a `dismissals` that is a string — and
 * the storage handle is resolved per call through an injected function, so
 * the module imports cleanly under a prerender and under vitest, neither of
 * which has `localStorage`.
 *
 * Total, and *directional*: within a record, every unreadable value resolves
 * towards not asking. A `dismissals` that is not a number reads as spent, an
 * `askedAt` that is not a string or `null` reads as a snooze that started just
 * now. A field we cannot read is not a field we may interrupt on the strength
 * of, and the failure it leaves — a visitor who installs from the browser's
 * own menu instead — is the one worth having. (The next `recordLoad` writes
 * that reading back, so a corrupt record settles into a stop rather than
 * flickering between one and the other.)
 *
 * The one thing that does *not* resolve that way is a payload that is no
 * record at all: an absent key, unparseable JSON, `null`, an array. Those read
 * as a browser that has never been here, because that is precisely what they
 * are indistinguishable from — and a browser that has never been here still
 * owes `MIN_LOADS` before anything can be asked of it.
 */
/** The slice of the Web Storage API this module reads and writes. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The browser's `localStorage`, or null where there isn't one. The try/catch
 * matters — in some privacy modes merely touching `localStorage` throws.
 */
export function resolveLocalStorage(): StorageLike | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

export const INSTALL_TUNING = {
  /** Document loads that must have happened before the offer may appear. */
  MIN_LOADS: 2,
  /** Stop counting past this — the only question is "more than MIN_LOADS". */
  LOADS_CAP: 99,
  /** How long an asking — shown, or waved away — silences the offer. */
  SNOOZE_MS: 30 * 24 * 60 * 60 * 1000,
  /** Dismissals after which the offer never returns. */
  MAX_DISMISSALS: 3,
  /**
   * How long the page is left alone before the offer slides in. Long enough
   * that whatever the visitor opened the page for has been rendered, read,
   * and started on; short enough that it does not arrive over a scroll.
   */
  APPEAR_DELAY_MS: 2200,
} as const;

export interface InstallState {
  /** Document loads counted so far, capped at `LOADS_CAP`. */
  readonly loads: number;
  /**
   * When the offer last took the visitor's attention — put on screen, or waved
   * away — ISO, or null if it never has. One timestamp rather than two because
   * both events buy exactly the same thing, silence for `SNOOZE_MS`, and two
   * fields with one meaning is two fields that can disagree.
   */
  readonly askedAt: string | null;
  /** How many times it has been waved away. */
  readonly dismissals: number;
}

export const DEFAULT_INSTALL_STATE: InstallState = {
  loads: 0,
  askedAt: null,
  dismissals: 0,
};

/**
 * A non-negative integer, or 0 for anything else at all.
 *
 * Zero is the safe reading for `loads`, and only for `loads`: fewer visits
 * than the visitor has really made means the offer waits longer, which is the
 * direction everything in this module errs in. The other two fields need the
 * opposite treatment, below.
 */
function readCount(value: unknown, cap: number): number {
  if (typeof value !== "number") return 0;
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(Math.floor(value), cap);
}

/**
 * `dismissals`, resolving anything unreadable to a full house.
 *
 * The asymmetry with `readCount` is the whole point. A `dismissals` that is
 * not a plain non-negative number is a value this module did not write — a
 * hand edit, a half-finished write, another tab's idea of the schema — and the
 * two ways of guessing wrong are not equal. Reading it as 0 hands a visitor
 * who has already said no three times a fresh set of three interruptions.
 * Reading it as `MAX_DISMISSALS` costs a visitor who never dismissed anything
 * a panel, and leaves them installing from the browser's own menu — which,
 * once the offer is silenced, is exactly where ./install-prompt.ts stops
 * getting in the browser's way.
 */
function readDismissals(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return INSTALL_TUNING.MAX_DISMISSALS;
  }
  return Math.min(Math.floor(value), INSTALL_TUNING.MAX_DISMISSALS);
}

/**
 * `askedAt`, resolving anything unreadable to "just now".
 *
 * Same direction, in the field where it is cheap: an unreadable timestamp
 * starts a fresh snooze rather than dropping the one that was there, so the
 * worst a corrupt record can do is delay the next asking. `null` is the one
 * non-string trusted, because `null` is what this module writes for "never
 * asked" — an absent field is not that, it is a record of unknown provenance,
 * and it is treated like any other unreadable value.
 */
function readAskedAt(value: unknown, now: Date): string | null {
  if (value === null) return null;
  if (typeof value === "string") return value;
  return now.toISOString();
}

/**
 * Read a stored record, total in the sense of the module comment.
 *
 * `now` is a parameter for the same reason the storage handle is: the corrupt
 * reading above stamps a timestamp, and a test that had to guess at the clock
 * could not assert which one.
 */
export function parseInstallState(
  raw: string | null,
  now: Date = new Date(),
): InstallState {
  if (raw === null) return DEFAULT_INSTALL_STATE;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_INSTALL_STATE;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return DEFAULT_INSTALL_STATE;
  }

  const record = parsed as Record<string, unknown>;
  return {
    loads: readCount(record.loads, INSTALL_TUNING.LOADS_CAP),
    askedAt: readAskedAt(record.askedAt, now),
    dismissals: readDismissals(record.dismissals),
  };
}

/**
 * One app's install record: where it lives, and the four writes the offer
 * makes to it.
 *
 * Every write returns the state it wrote rather than void, so a caller can
 * decide in the same breath whether the offer is due, without a second read
 * that would have to agree with the write.
 */
export interface InstallStateStore {
  /** The storage key, for an app that has to name it (a reset, a test). */
  readonly key: string;
  read(now?: Date): InstallState;
  /** Count this document load. */
  recordLoad(): InstallState;
  /**
   * The offer was actually put in front of the visitor. Starts the snooze.
   *
   * No strike, because nothing has been answered: this is the record that the
   * asking *happened*, which is the only thing that stops an offer nobody
   * ever touches from arriving on every load until it is finally clicked. The
   * three strikes stay reserved for the visitor saying no in as many words.
   */
  recordImpression(at?: Date): InstallState;
  /** The visitor waved the offer away. Restarts the snooze, spends a strike. */
  recordDismissal(at?: Date): InstallState;
  /**
   * The offer is spent — the visitor installed, or told us no for the last
   * time.
   *
   * Burning every remaining strike rather than adding a fourth field: "never
   * ask again" and "asked three times" want identical behaviour forever, and
   * one predicate that reads one number cannot disagree with itself.
   */
  retire(): InstallState;
}

/**
 * The store for one app's install record under `key`.
 *
 * Make exactly one per app, at module scope, and pass that same object to
 * `watchInstallability` and `useInstallOffer`: the hook keys an effect on it,
 * and the capture reads the policy through it.
 *
 * `storage` is called on every read and write rather than once here, so a
 * store built at module scope under a prerender (no `localStorage`) still
 * finds the real one in the browser.
 */
export function createInstallStateStore(
  key: string,
  storage: () => StorageLike | null = resolveLocalStorage,
): InstallStateStore {
  function read(now: Date = new Date()): InstallState {
    const handle = storage();
    if (handle === null) return DEFAULT_INSTALL_STATE;
    try {
      return parseInstallState(handle.getItem(key), now);
    } catch {
      return DEFAULT_INSTALL_STATE;
    }
  }

  function write(next: InstallState): InstallState {
    const handle = storage();
    if (handle === null) return next;
    try {
      handle.setItem(key, JSON.stringify(next));
    } catch {
      // Quota, or a private-mode write failure. The offer simply behaves as
      // if this were a first visit next time, which is the failure we want:
      // it under-asks rather than over-asks.
    }
    return next;
  }

  return {
    key,
    read,
    recordLoad() {
      const state = read();
      return write({
        ...state,
        loads: Math.min(state.loads + 1, INSTALL_TUNING.LOADS_CAP),
      });
    },
    recordImpression(at = new Date()) {
      return write({ ...read(at), askedAt: at.toISOString() });
    },
    recordDismissal(at = new Date()) {
      const state = read(at);
      return write({
        ...state,
        askedAt: at.toISOString(),
        dismissals: Math.min(
          state.dismissals + 1,
          INSTALL_TUNING.MAX_DISMISSALS,
        ),
      });
    },
    retire() {
      return write({ ...read(), dismissals: INSTALL_TUNING.MAX_DISMISSALS });
    },
  };
}

/**
 * Has this offer already had its turn?
 *
 * The snooze and the hard stop — the two rules that are about what the visitor
 * has already been shown and already said — with the load count deliberately
 * left out. That is exactly the question ./install-prompt.ts has to answer at
 * `beforeinstallprompt` time, before it decides whether it is entitled to take
 * the browser's own install prompt away from them, and it cannot use
 * `isOfferDue`: this visit's load has not been counted yet at that point (the
 * hook counts it, from an effect), so a returning visitor would look one load
 * short and the browser would be freed to ask on the very visit the page is
 * about to ask on.
 */
export function isOfferSilenced(
  state: InstallState,
  now: Date = new Date(),
): boolean {
  if (state.dismissals >= INSTALL_TUNING.MAX_DISMISSALS) return true;
  if (state.askedAt === null) return false;

  const askedAt = Date.parse(state.askedAt);
  // A timestamp we cannot parse is one we did not write. Treat it as an asking
  // that has not expired rather than as no asking at all: the conservative
  // reading of "we asked once" is "do not ask again yet".
  if (Number.isNaN(askedAt)) return true;

  return now.getTime() - askedAt < INSTALL_TUNING.SNOOZE_MS;
}

/** Have the three rules in the module comment all been satisfied? */
export function isOfferDue(
  state: InstallState,
  now: Date = new Date(),
): boolean {
  if (state.loads < INSTALL_TUNING.MIN_LOADS) return false;
  return !isOfferSilenced(state, now);
}
